/**
 * Centralized Domain Validation and Management Utilities
 * This file contains all domain-related logic to avoid duplication
 */

import { config } from "../config";

/**
 * Configuration for domain validation
 */
const DOMAIN_CONFIG = {
  // Main Zoobbe domains
  mainDomains: [
    'zoobbe.com',
    // 'www.zoobbe.com'
  ],

  // Legitimate Zoobbe subdomains (not white label)
  legitimateSubdomains: [
    'app.zoobbe.com',
    // 'www.zoobbe.com',
    'beta.zoobbe.com',
    'api.zoobbe.com',
    'api-v1.zoobbe.com',
    'admin.zoobbe.com',
    'cdn.zoobbe.com',
    'assets.zoobbe.com',
    'mail.zoobbe.com',
    'support.zoobbe.com',
    'help.zoobbe.com',
    'docs.zoobbe.com',
    'blog.zoobbe.com',
    'status.zoobbe.com',
    'dev.zoobbe.com',
    'staging.zoobbe.com',
    'prod.zoobbe.com',
    'production.zoobbe.com',
    'demo.zoobbe.com',
    'sandbox.zoobbe.com',
    'portal.zoobbe.com'
  ],

  // Development domains
  developmentDomains: [
    'localhost',
    '127.0.0.1'
  ],

  // Default redirect domain
  defaultRedirectDomain: 'https://zoobbe.com'
};

/**
 * Get current domain/hostname
 */
export const getCurrentDomain = () => {
  if (typeof window === 'undefined') return null;
  return window.location.hostname;
};

/**
 * Check if current domain is localhost or development
 */
export const isLocalDevelopment = () => {
  const domain = getCurrentDomain();
  if (!domain) return false;

  // Check for localhost variations
  const isLocalhost = domain === 'localhost' || domain.startsWith('localhost:');
  const is127 = domain === '127.0.0.1' || domain.startsWith('127.0.0.1:');
  const isConfigDev = DOMAIN_CONFIG.developmentDomains.includes(domain);

  // Additional checks for common development patterns
  const isLocalIP = domain.startsWith('192.168.') || domain.startsWith('10.') || domain.startsWith('172.');
  const isDevDomain = domain.includes('.local') || domain.includes('.dev');

  const result = isLocalhost || is127 || isConfigDev || isLocalIP || isDevDomain;

  return result;
};

/**
 * Check if domain is a legitimate Zoobbe subdomain
 */
export const isLegitimateZoobbeDomain = (domain = getCurrentDomain()) => {
  if (!domain) return false;
  return DOMAIN_CONFIG.legitimateSubdomains.includes(domain);
};

/**
 * Check if domain is a main Zoobbe domain
 */
export const isMainZoobbeDomain = (domain = getCurrentDomain()) => {
  if (!domain) return false;
  return DOMAIN_CONFIG.mainDomains.includes(domain);
};

/**
 * Check if domain is a potential white label domain
 */
export const isPotentialWhiteLabelDomain = (domain = getCurrentDomain()) => {
  if (!domain) return false;

  // Development domains are not white label
  if (isLocalDevelopment()) return false;

  // Main Zoobbe domains are not white label
  if (isMainZoobbeDomain(domain)) return false;

  // Legitimate subdomains are not white label
  if (isLegitimateZoobbeDomain(domain)) return false;

  // Check if it's a subdomain of zoobbe.com but not a legitimate one
  if (domain.endsWith('.zoobbe.com')) {
    return true;
  }

  // Check if it's a custom domain (not zoobbe.com at all)
  if (!domain.includes('zoobbe.com')) {
    return true;
  }

  return false;
};

/**
 * Check if domain has access to Zoobbe
 */
export const isDomainAllowed = (domain = getCurrentDomain()) => {
  if (!domain) return false;

  // Allow development domains
  if (isLocalDevelopment()) return true;

  // Allow main Zoobbe domains
  if (isMainZoobbeDomain(domain)) return true;

  // Allow legitimate subdomains
  if (isLegitimateZoobbeDomain(domain)) return true;

  // Allow potential white label domains (they will be validated by backend)
  if (isPotentialWhiteLabelDomain(domain)) return true;

  return false;
};

/**
 * Check if domain should have X-Original-Host header for white label
 */
export const shouldAddWhiteLabelHeader = (domain = getCurrentDomain()) => {
  if (!domain) return false;

  // Don't add header for development domains
  if (isLocalDevelopment()) return false;

  // Don't add header for main Zoobbe domains
  if (isMainZoobbeDomain(domain)) return false;

  // Don't add header for legitimate subdomains
  if (isLegitimateZoobbeDomain(domain)) return false;

  // Add header for potential white label domains
  return isPotentialWhiteLabelDomain(domain);
};

/**
 * Get subdomain from current hostname
 */
export const getCurrentSubdomain = (domain = getCurrentDomain()) => {
  if (!domain) return null;

  if (domain.endsWith('.zoobbe.com')) {
    return domain.split('.')[0];
  }

  return null;
};

/**
 * Validate domain format
 */
export const validateDomainFormat = (domain) => {
  if (!domain) {
    return { valid: false, error: 'Domain is required' };
  }

  if (domain.includes('zoobbe.com')) {
    return { valid: false, error: 'Cannot use zoobbe.com domains' };
  }

  // Basic format check
  const hostnameRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
  if (!hostnameRegex.test(domain)) {
    return { valid: false, error: 'Invalid domain format' };
  }

  return { valid: true };
};

/**
 * Get redirect URL for unauthorized domains
 */
export const getRedirectUrl = (unauthorizedDomain = getCurrentDomain()) => {
  const baseUrl = DOMAIN_CONFIG.defaultRedirectDomain;
  
  if (unauthorizedDomain && unauthorizedDomain !== getCurrentDomain()) {
    return `${baseUrl}?unauthorized_domain=${encodeURIComponent(unauthorizedDomain)}`;
  }
  
  return baseUrl;
};

/**
 * Create headers object with appropriate white label headers
 */
export const createRequestHeaders = (additionalHeaders = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...additionalHeaders
  };

  // Add X-Original-Host header for white label domains
  if (shouldAddWhiteLabelHeader()) {
    headers['X-Original-Host'] = getCurrentDomain();
  }

  return headers;
};

/**
 * Check domain access with backend validation
 */
export const checkDomainAccess = async () => {
  const currentDomain = getCurrentDomain();

  // Allow development domains immediately - no API calls needed
  if (isLocalDevelopment()) {
    return true;
  }

  // Allow main Zoobbe domains immediately
  if (isMainZoobbeDomain(currentDomain) || isLegitimateZoobbeDomain(currentDomain)) {
    console.log('🔍 Legitimate Zoobbe domain detected, allowing access:', currentDomain);
    return true;
  }

  // For potential white label domains, check with backend
  if (isPotentialWhiteLabelDomain(currentDomain)) {
    console.log('🔍 Potential white label domain detected, checking with backend:', currentDomain);
    try {
      const headers = createRequestHeaders();
      const response = await fetch(`${config.API_URI}/v1/white-label/branding`, {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 White label API response:', data);
        return !!data.isWhiteLabel;
      } else if (response.status === 403) {
        // Handle unauthorized domain access
        try {
          const errorData = await response.json();
          if (errorData.redirectTo) {
            console.log('Unauthorized domain detected, redirecting to:', errorData.redirectTo);
            window.location.href = errorData.redirectTo;
            return false;
          }
        } catch (e) {
          console.error('Error parsing 403 response:', e);
        }
        return false;
      }
    } catch (error) {
      console.error('Error checking domain access:', error);
      // For network errors, allow access to prevent blocking legitimate users
      console.log('🔍 Network error occurred, allowing access to prevent blocking');
      return true;
    }
  }

  console.log('🔍 Domain not recognized, denying access:', currentDomain);
  return false;
};

/**
 * Redirect to main domain if access denied
 */
export const redirectIfUnauthorized = () => {
  const redirectUrl = getRedirectUrl();
  window.location.href = redirectUrl;
};

export default {
  getCurrentDomain,
  isLocalDevelopment,
  isLegitimateZoobbeDomain,
  isMainZoobbeDomain,
  isPotentialWhiteLabelDomain,
  isDomainAllowed,
  shouldAddWhiteLabelHeader,
  getCurrentSubdomain,
  validateDomainFormat,
  getRedirectUrl,
  createRequestHeaders,
  checkDomainAccess,
  redirectIfUnauthorized,
  DOMAIN_CONFIG
};
